// ** MUI Imports
import { Adsense } from '@ctrl/react-adsense'
import { StarBorder } from '@mui/icons-material'
import { Alert, Box, Button, Divider, Snackbar, Stack } from '@mui/material'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { getCookie } from 'cookies-next'
import { EditorState, RawDraftContentState, convertFromRaw, convertToRaw } from 'draft-js'
import { BookPlusOutline, EyeOutline } from 'mdi-material-ui'
import { GetServerSidePropsContext } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import ConfirmDialog from 'src/@core/components/dialogs/ConfirmDialog'
import BookApi from 'src/@core/utils/api/book-api'
import UserApi from 'src/@core/utils/api/user-api'
import { getBookCoverImage, numberFormatCompact, sendEvent, tryOrGet } from 'src/@core/utils/functions'
import { AlertState, BookData, Chapter } from 'src/@core/utils/types'
import BookChapterSelect from 'src/views/book/BookChapterSelect'
import BookCoverImage from 'src/views/book/BookCoverImage'
import ContentRendererComponent from 'src/views/book/ContentRendererComponent'
import LeadCard from 'src/views/lead/LeadCard'

type BookViewPageProps = {
  chapterList: Chapter[]
  book: BookData
  chapterNumber: number
  rawContent: RawDraftContentState
}
const BookViewPage = ({ chapterList, book, chapterNumber, rawContent }: BookViewPageProps) => {
  const router = useRouter()
  const sessionStart = useMemo(() => Date.now(), [])
  const [showSynopsis, setShowSynopsis] = useState(false)
  const [pageNumber, setPageNumber] = useState<number>()
  const [showConfirmAddToLibraryDialog, setShowConfirmAddToLibraryDialog] = useState(false)
  const [alertState, setAlertState] = useState<AlertState>({ show: false })

  useEffect(() => {
    if (pageNumber !== chapterNumber) {
      setPageNumber(chapterNumber)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterNumber])

  useEffect(() => {
    if (book) {
      BookApi.logBookView(book._id as string)
      sendEvent('view_book', {
        'book_id': book._id,
        'time_wait': Date.now() - sessionStart
      })
    }
  }, [book, sessionStart])

  const addBookToLibrary = async () => {
    setShowConfirmAddToLibraryDialog(false)
    try {
      await UserApi.addBookToLibrary(book._id as string)
      setAlertState({ show: true, success: true, message: `The book ${book.title} was added to your library.` })
    } catch (e: any) {
      if (e?.response?.status === 401) {
        router.push('/login/')

        return
      }
      setAlertState({ show: true, success: false, message: `There was an error in our server. Please, try again...` })
    }
  }

  const handleCloseFormUpdateAlert = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }

    setAlertState({ show: false })
  }

  if (!book) {
    return null;
  }

  return (
    <>
      <Head>
        <title key={'title'}>{`Book - ${book.title}`}</title>
        <meta key='description' name='description' content={`${book.description}`} />
        <meta key='og:description' property='og:description' content={`${book.description}`} />

        <meta key='fb:app_id' property='fb:app_id' content='301156096104245' />
        <meta key='twitter:site' name='twitter:site' content='@inknovl' />
        <meta key='twitter:image:alt' name='twitter:image:alt' content={`${book.title} cover`} />

        <meta key='og:title' property='og:title' content={`${book.title}`} />
        <meta key='og:type' property='og:type' content='book' />
        <meta key='og:image' property='og:image' content={`${getBookCoverImage(book, true)}`} />
        <meta key='og:image:alt' name='og:image:alt' content={`${book.title} cover`} />
        <meta
          key='og:url'
          property='og:url'
          content={`${process.env.NEXT_PUBLIC_DOMAIN}/book/${book._id}/${chapterNumber}/`}
        />
        <meta key='twitter:card' name='twitter:card' content='summary_large_image' />
        <link rel='canonical' href={`${process.env.NEXT_PUBLIC_DOMAIN}/book/${book._id}/${chapterNumber}/`} />
      </Head>
      <Grid container spacing={6}>
        <Grid item sx={{ display: 'flex' }}>
          <Stack direction={'row'}>
            <BookCoverImage book={book} hoverEffect={false} />
            <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Stack direction={'row'} justifyContent={'space-between'}>
                <Typography variant='h2' sx={{ lineHeight: '0.9' }}>
                  {book.title}
                </Typography>
              </Stack>
              <Stack spacing={2} pt={1} direction='row' divider={<Divider orientation='vertical' flexItem />}>
                <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center' }}>
                  {book.author}
                </Typography>
                <Typography variant='caption'>{book.genre}</Typography>
              </Stack>
              <Stack spacing={2} pt={1} direction='row'>
                <EyeOutline sx={{ fontSize: '1rem' }} />
                <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center' }}>
                  {numberFormatCompact(book.readCount as number)}
                </Typography>
                <StarBorder sx={{ fontSize: '1rem' }} />
                <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center' }}>
                  {book.rating}
                </Typography>
              </Stack>
              <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center' }}>
                {`${book.description?.substring(0, 180)}`}
                {book.description && book.description?.length > 180 && <>
                  {'...'}
                  <Button sx={{ textTransform: 'none' }} onClick={() => setShowSynopsis(true)}>
                    synopsis
                  </Button>
                </>}

              </Typography>
              <Box flexGrow={1} alignItems={'end'} display={'flex'}>
                <Button
                  startIcon={<BookPlusOutline />}
                  variant='outlined'
                  onClick={() => setShowConfirmAddToLibraryDialog(true)}>
                  Add To Library
                </Button>
              </Box>
            </Box>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <BookChapterSelect chapterList={chapterList} selectedChapter={chapterNumber} />
        </Grid>
        <Grid xs={12} item>
          <Adsense client={process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_ID as string} slot={process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_SLOT as string} responsive={'true'} />
        </Grid>
        <Grid item xs={12}>
          {rawContent && <ContentRendererComponent
            editorState={EditorState.createWithContent(convertFromRaw(rawContent))}
            onChange={() => {
              return
            }}
          />}
        </Grid>
        {chapterNumber < chapterList.length && (
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant={'outlined'}
              href={`/book/${book._id}/${chapterNumber + 1}/`}
              onClick={e => {
                e.preventDefault()
                router.push(`/book/${book._id}/${chapterNumber + 1}/`)
              }}
            >
              Continue to the next chapter
            </Button>
          </Grid>
        )}
        <Grid item xs={12}>
          <Adsense client={process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_ID as string} slot={process.env.NEXT_PUBLIC_GOOGLE_ADSENSE_SLOT as string} responsive={'true'} />
        </Grid>
        {alertState.show && (
          <Snackbar
            open={alertState.show}
            autoHideDuration={4000}
            onClose={handleCloseFormUpdateAlert}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <Alert
              onClose={handleCloseFormUpdateAlert}
              severity={alertState.success ? 'success' : 'error'}
              variant={'filled'}
              elevation={6}
              sx={{ width: '100%' }}
            >
              {alertState.message}
            </Alert>
          </Snackbar>
        )}
        {showConfirmAddToLibraryDialog && (
          <ConfirmDialog
            open={showConfirmAddToLibraryDialog}
            dialogTitle={'Add to library'}
            contentText={`Do you want to add the book ${book.title} to your library?`}
            confirmText={'Add Book'}
            cancelText={'Cancel'}
            onCancel={() => setShowConfirmAddToLibraryDialog(false)}
            onConfirm={addBookToLibrary}
          ></ConfirmDialog>
        )}
        {showSynopsis && (
          <ConfirmDialog
            open={showSynopsis}
            dialogTitle={`Synopsis: ${book.title}`}
            contentText={book?.description || ''}
            confirmText={'Close'}
            onConfirm={() => setShowSynopsis(false)}
          ></ConfirmDialog>
        )}
        <LeadCard timeout={5000} />
      </Grid>
    </>
  )
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  const bookId = context.params.id
  const chapterNumber = tryOrGet(() => parseInt(context.params.chapterNumber as string) as number, 1)
  const token = getCookie('token', { req: context.req, res: context.res })
  const options = { headers: { Authorization: `Bearer ${token}` } };
  const chapterList = await BookApi.getPublishedBookChapters(bookId, options)
  const book = await BookApi.getPublishedBook(bookId, options)
  const bookPage = await BookApi.getBookChapter(bookId, chapterNumber, options)
  let rawContent: RawDraftContentState = convertToRaw(EditorState.createEmpty().getCurrentContent())

  if (bookPage.content) {
    rawContent = bookPage.content as RawDraftContentState
    if (!rawContent.entityMap) {
      rawContent.entityMap = {}
    }
  }

  return { props: { chapterList, book, chapterNumber, rawContent } }
}

export default BookViewPage
